/**
 * useBranchInfo — small read-only hooks that surface delivery / ETA / minimum
 * order metadata from the branch config.
 *
 * Field names below are taken from the wire payload returned by
 * `configApi.get(slug)` even when they are not yet declared on the generated
 * `BranchConfig` SDK type — that's why we cast to a permissive local shape.
 *
 * Sensible defaults are returned when the field is absent so that the UI
 * never collapses to "0 min" or shows a $NaN delivery fee.
 */
import { useBranch } from "@/store/branch";
import { useFulfillment } from "@/store/fulfillment";
import { pricingAmount } from "@/lib/fulfillment";

/**
 * Branch-configured prep or delivery window as `[etaMin, etaMax]` minutes, or
 * nulls when the branch has no configured value. Pre-order surfaces only
 * (cart, legacy checkout copy); placed orders render their promise. Promise
 * Time removed the invented 18-minute default: an unknown time is shown as
 * nothing, not as a guess.
 */
export function useBranchEta(): { etaMin: number | null; etaMax: number | null } {
  const { branch } = useBranch();
  const { mode } = useFulfillment();
  const b = branch as
    | { estimatedKitchenPreparationMinutes?: number | null; estimatedDeliveryDurationMinutes?: number | null }
    | undefined;

  if (mode === "delivery" && typeof b?.estimatedDeliveryDurationMinutes === "number") {
    const d = b.estimatedDeliveryDurationMinutes;
    return { etaMin: d, etaMax: d + 10 };
  }
  if (typeof b?.estimatedKitchenPreparationMinutes === "number") {
    const prep = b.estimatedKitchenPreparationMinutes;
    return { etaMin: prep, etaMax: prep + 10 };
  }
  return { etaMin: null, etaMax: null };
}

/** The branch's dine-in service charge percent (0 when it has none). */
export function useDineInServiceChargePercent(): number {
  const { branch } = useBranch();
  const b = branch as { dineInServiceChargePercent?: number | null } | undefined;
  const percent = Number(b?.dineInServiceChargePercent ?? 0);
  return Number.isFinite(percent) && percent > 0 ? percent : 0;
}

/**
 * The branch's active dine-in service-fee taxes (settings taxes with scope
 * `service_fee`), as the BFF exposes them on the branch config. Accepts
 * `serviceFeeTaxes` (array) or a single `serviceFeeTax`. Empty when the
 * branch config does not carry them (the fee is then priced untaxed).
 */
export function useServiceFeeTaxes(): any[] {
  const { branch } = useBranch();
  const b = branch as { serviceFeeTaxes?: any[] | null; serviceFeeTax?: any | any[] | null } | undefined;
  const raw = b?.serviceFeeTaxes ?? b?.serviceFeeTax ?? [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.filter((t) => t && t.isActive !== false && t.isDeleted !== true);
}

/** Delivery fee for the resolved branch (in the branch currency). */
export function useDeliveryFee(): number {
  const { branch } = useBranch();
  const b = branch as
    | { deliveryFee?: unknown }
    | undefined;
  return pricingAmount(b?.deliveryFee);
}

/** Delivery taxes for the resolved branch. */
export function useDeliveryTaxes(): any[] {
  const { branch } = useBranch();
  const b = branch as
    | { deliveryTax?: any[] }
    | undefined;
  return b?.deliveryTax || [];
}

/** Minimum order value for the resolved branch (in the branch currency). */
export function useMinOrder(): number {
  const { branch } = useBranch();
  const b = branch as
    | { minimumOrderAmount?: number | null; minimumOrderValue?: number | null }
    | undefined;
  if (typeof b?.minimumOrderAmount === "number" && b.minimumOrderAmount > 0) {
    return b.minimumOrderAmount;
  }
  if (typeof b?.minimumOrderValue === "number" && b.minimumOrderValue > 0) {
    return b.minimumOrderValue;
  }
  return 0;
}

import { useEffect } from "react";
import { branchesApi } from "@/api/services";
import { useAddresses } from "@/hooks/useAddresses";

/** Automatically looks up the nearest branch when the delivery address changes and saves it to the branch context. */
export function useSyncNearestBranch() {
  const { selectedAddressId, manualAddress, mode } = useFulfillment();
  const { addresses } = useAddresses();
  const { branchConfig, selectBranch, branch, branchId } = useBranch();

  useEffect(() => {
    if (mode !== "delivery") return;
    const selectedAddress = (selectedAddressId ? addresses.find((a) => a.id === selectedAddressId) : null) ?? manualAddress;
    const coords = selectedAddress?.coordinates;
    const urlPath = branchConfig?.urlPath;
    if (!coords || !urlPath) return;

    let cancelled = false;
    branchesApi
      .findNearest(coords.latitude, coords.longitude, urlPath, {
        limit: 1,
        serviceType: "delivery",
      })
      .then((nearest) => {
        if (cancelled) return;
        const nearestBranch = nearest.branches?.[0] ?? null;
        if (nearestBranch?.id) {
          // Prevent infinite loops or redundant state updates
          const isSame = branchId === nearestBranch.id && (branch as any)?.deliveryFee?.amount === nearestBranch.deliveryFee?.amount;
          if (!isSame) {
            selectBranch(nearestBranch.id, nearestBranch);
          }
        }
      })
      .catch((err) => {
        console.warn("[syncNearest] branch lookup failed", err);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressId, manualAddress, addresses, branchConfig?.urlPath, mode, selectBranch, branchId, branch]);
}
